import classNames from "classnames"

import BoxContent from "./BoxContent"
import ProductRating from "../Widgets/ProductRating"

import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import PriceDisplay from "@components/elements/V2/PriceDisplay"
import Typography from "@components/elements/V2/Typography"
import RichTextElement from "@components/StoryBlok/RichTextElement"
import { PayflexSection } from "@components/V2/Sections/Info/Payflex/PayflexSection"
import TagDisplay from "@components/V2/Sections/Info/TagDisplay"
import { Color } from "@constants/V2/color"
import { Industry } from "@constants/V2/industry"
import { hasRichTextContent } from "@utils/richtext"
import { getBackgroundColorClass } from "@utils/V2/color"
import { getIndustryName } from "@utils/V2/industry"
import React from "react"

type Props = {
  name: string
  price: string
  originalPrice?: string
  priceSubscriptText?: string
  onPromotion?: boolean
  overrideShowDiscount?: boolean
  showRatings: boolean
  skus?: string[]
  tags?: Storyblok.ProductTag[]
  description?: string
  descriptionHeader?: string
  paymentStrip?: string
  boxContent?: {
    title: string
    imageUrl?: string
    imageAlt?: string
  }[]
  disclaimer?: {
    richText?: Storyblok.RichTextBlock
    color: Color
  }
  industries?: Industry[]
  suitedFor?: string
  worksWith?: string[]
  variantSelector?: React.ReactNode
  variantSkus?: string[][]
  variantDescription?: string
}

const ProductDetails = ({
  name,
  price,
  originalPrice,
  priceSubscriptText,
  onPromotion,
  overrideShowDiscount,
  showRatings,
  skus,
  tags,
  description,
  descriptionHeader,
  paymentStrip,
  boxContent,
  disclaimer,
  industries,
  suitedFor,
  worksWith,
  variantSelector,
  variantSkus,
  variantDescription,
}: Props) => {
  const selectedRatingKey = (skus ?? []).join(";")
  const ratingSkusByKey = new Map<string, string[]>()

  for (const ratingSkus of [...(variantSkus ?? []), skus ?? []]) {
    ratingSkusByKey.set(ratingSkus.join(";"), ratingSkus)
  }

  return (
    <>
      <div className="py-xs -mt-8 flex flex-col gap-y-8 sm:-mt-0 lg:gap-y-16">
        <div className="flex items-center justify-between pb-12 md:pb-0">
          <div className="flex w-full flex-col gap-2">
            <Typography
              text={name}
              font="grotesk"
              color={Color.Charcoal}
              size="lead-lg"
              weight="book"
              element="h1"
            />
            <div className="flex">
              <PriceDisplay
                productPrices={{
                  price,
                  originalPrice,
                }}
                priceFontSize="lead-lg"
                priceFontWeight="medium"
                superscriptFontsize="body-sm"
                subscriptClassName="mt-0"
                overrideShowDiscount={overrideShowDiscount}
                onPromotion={onPromotion}
                className="lg:items-start"
                originalPriceColor={Color.Charcoal}
                originalPricePosition="right"
                showDecimals={false}
              />

              <PayflexSection
                version="simple"
                price={Number.parseFloat(price)}
              />
            </div>
            {priceSubscriptText && (
              <div>
                <Typography
                  text={priceSubscriptText}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-sm"
                  className="mt-0"
                  weight="book"
                />
              </div>
            )}
            {showRatings &&
              [...ratingSkusByKey].map(([ratingKey, ratingSkus]) => (
                <div
                  key={ratingKey}
                  className={classNames({
                    hidden: ratingKey !== selectedRatingKey,
                  })}
                >
                  <ProductRating color={Color.Blue} skus={ratingSkus} />
                </div>
              ))}
            {variantSelector && <div className="mt-16">{variantSelector}</div>}
            <TagDisplay tags={tags} className="mt-14" />
            {variantDescription && (
              <Typography
                text={variantDescription}
                font="grotesk"
                color={Color.Charcoal}
                size="body-md"
                weight="book"
              />
            )}
          </div>
        </div>

        <hr className="my-12 border-black opacity-20" />

        {descriptionHeader && (
          <Typography
            text={descriptionHeader}
            font="grotesk"
            color={Color.Charcoal}
            size="body-lg"
            weight="medium"
            className="pt-8"
          />
        )}
        <Typography
          text={description || "No product description available"}
          font="grotesk"
          color={Color.Charcoal}
          size="body-md"
          weight="book"
        />
        {worksWith && worksWith.length > 0 && (
          <div className="my-8 flex flex-wrap items-center gap-10">
            <Typography
              text="Works with:"
              font="grotesk"
              color={Color.Charcoal}
              size="body-md"
              weight="medium"
            />
            {worksWith.map((item, index) => (
              <div
                key={`${item}-${index}`}
                className="rounded-[6px] px-10 py-8 ring-1 ring-inset ring-charcoal/20"
              >
                <Typography
                  text={item}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-sm"
                  weight="medium"
                />
              </div>
            ))}
          </div>
        )}
        {paymentStrip && (
          <div className="flex gap-16">
            <Typography
              text="Accepts:"
              font="grotesk"
              color={Color.Charcoal}
              size="body-md-fixed"
              weight="medium"
              className="pt-4"
            />
            <GatsbyStoryblokImage
              className="h-auto w-full !max-w-[240px] sm:w-auto"
              alt="payment strip"
              image={paymentStrip}
            />
          </div>
        )}
      </div>

      {boxContent && boxContent.length > 0 && (
        <>
          <hr className="my-12 border-black opacity-20" />

          <div className="py-xs flex flex-col gap-y-8 lg:gap-y-16">
            <BoxContent boxContent={boxContent} />
          </div>
        </>
      )}

      {disclaimer && hasRichTextContent(disclaimer?.richText) && (
        <>
          <div
            className={classNames(
              "mb-32 mt-12 rounded-8 bg-opacity-10 px-20 py-12 text-center",
              getBackgroundColorClass(disclaimer.color)
            )}
          >
            <RichTextElement
              content={disclaimer.richText}
              className="[&_p]:!pb-0 [&_p]:text-body-sm"
            />
          </div>

          <hr className="my-12 border-black opacity-20" />
        </>
      )}

      {industries && industries.length > 0 && (
        <>
          <div className="py-xs flex flex-col gap-y-16">
            <div className="flex flex-col gap-y-8">
              <Typography
                text="Best suited for:"
                font="grotesk"
                color={Color.Charcoal}
                size="body-lg"
                weight="medium"
              />
              {suitedFor && (
                <Typography
                  text={suitedFor}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-md"
                  weight="book"
                />
              )}
            </div>
            <div className="flex flex-wrap gap-10">
              {industries.map((industry) => (
                <div
                  key={industry}
                  className="rounded-8 bg-light-gray px-12 py-10"
                >
                  <Typography
                    text={getIndustryName(industry)}
                    font="grotesk"
                    color={Color.Charcoal}
                    size="body-sm"
                    weight="book"
                  />
                </div>
              ))}
            </div>
          </div>

          <hr className="my-12 border-black opacity-20" />
        </>
      )}
    </>
  )
}

export default ProductDetails
